import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { DeficiencyTicketWatcher } from 'src/app/_model/deficiency-ticket-watcher';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { IMSId } from 'src/app/_model/ims-object';

import { DeficiencyTicketWatchersService } from '../deficiency-ticket-watchers.service';
import { DeficiencyTicketWatchersDaoService } from '../../dao/deficiency-ticket-watchers-dao.service';
import { DeficiencyTicketsService } from 'src/app/_services/deficiency-ticket/logic/deficiency-tickets.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads the ims/DeficiencyTicket <-> watcher mapping for the deficiency
 * tickets currently available in the app
 */
@Injectable()
export class DeficiencyTicketWatchersLogicService implements DeficiencyTicketWatchersService {
  public deficiencyTicketWatchers$ = new BehaviorSubject<DeficiencyTicketWatcher[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private deficiencyTicketWatchersDaoService: DeficiencyTicketWatchersDaoService,
    private authenticationService: AuthenticationService,
    private deficiencyTicketsService: DeficiencyTicketsService,
  ) {
    this.initialize();
  }

  public getWatchersByDeficiencyTicketId(id: IMSId): DeficiencyTicketWatcher[] {
    if (id == undefined) {
      return [];
    }
    // deficiencyTicketId is stored as DeficiencyTicket.getImsId() (imsId once
    // synced, secondaryId while still local) - match against either.
    return this.deficiencyTicketWatchers$.getValue().filter((watcher) => {
      return (
        (id.imsId != null && watcher.deficiencyTicketId === id.imsId) ||
        (id.secondaryId != null && watcher.deficiencyTicketId === id.secondaryId)
      );
    });
  }

  /**
   * Creates a new, purely local DeficiencyTicket<->watcher mapping (not sent to
   * the backend) - kept available until the backend syncs the proper mapping back.
   */
  public async createDeficiencyTicketWatcher(entry: DeficiencyTicketWatcher): Promise<DeficiencyTicketWatcher> {
    const created = await this.deficiencyTicketWatchersDaoService.createDeficiencyTicketWatcher(entry);
    if (created) {
      this.deficiencyTicketWatchers$.next([...this.deficiencyTicketWatchers$.getValue(), created]);
    }
    return created;
  }

  // Resetable
  public async reset() {
    // Unlike clear(), reset() also wipes locally created (not-yet-synced) watchers.
    await this.deficiencyTicketWatchersDaoService.reset();
    const remaining = await this.deficiencyTicketWatchersDaoService.getDeficiencyTicketWatchers();
    this.deficiencyTicketWatchers$.next(remaining);
  }

  public async clear() {
    // Only truncates the synced table (see DAO) - reload so locally created
    // watchers that survive the clear stay visible in the BehaviorSubject.
    await this.deficiencyTicketWatchersDaoService.clear();
    const remaining = await this.deficiencyTicketWatchersDaoService.getDeficiencyTicketWatchers();
    this.deficiencyTicketWatchers$.next(remaining);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteDeficiencyTicketWatchers(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalDeficiencyTicketWatchers();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  async initializeCompleted(): Promise<void> {}

  // Private Functions
  private async loadLocalDeficiencyTicketWatchers(): Promise<void> {
    try {
      this.logger.log(this.constructor.name, '- Load local deficiency ticket watchers');
      const entries = await this.deficiencyTicketWatchersDaoService.getDeficiencyTicketWatchers();
      this.deficiencyTicketWatchers$.next(entries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local deficiency ticket watchers: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteDeficiencyTicketWatchers(apiLevel: APILevel, syncKey: string): Promise<void> {
    if (!this.authenticationService.authenticationState.getValue()) {
      return;
    }

    if (apiLevel < APILevel.PROJECT_CONTACT_DIRECTORY) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to sync deficiency ticket watchers',
      );
      return;
    }

    this.logger.log(this.constructor.name, '- Load new deficiency ticket watchers from IMS');

    try {
      const deficiencyTicketIdList = this.deficiencyTicketsService.deficiencyTickets$
        .getValue()
        .map((deficiencyTicket) => deficiencyTicket.getImsId())
        .join(',');

      const entries = await this.loadRemoteDeficiencyTicketWatcherBatch(
        syncKey,
        deficiencyTicketIdList,
        [],
      );

      this.logger.log(
        this.constructor.name,
        'Finished loading all deficiency ticket watchers (' + entries.length + ' Total)',
      );

      await this.loadLocalDeficiencyTicketWatchers();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error downloading deficiency ticket watchers: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteDeficiencyTicketWatcherBatch(
    syncKey: string,
    deficiencyTicketIdList: string,
    entries: DeficiencyTicketWatcher[],
    nextPart?: string,
  ): Promise<DeficiencyTicketWatcher[]> {
    let data: any;
    if (nextPart) {
      data = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey,
          },
        }),
      );
    } else {
      data = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncdeficiencyticketwatchers',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            deficiencyticketidlist: deficiencyTicketIdList,
            synckey: syncKey,
          },
        }),
      );
    }

    let nextId: string;
    const batchEntries: DeficiencyTicketWatcher[] = [];
    for (const item of data) {
      if (item['#nextid']) {
        nextId = item['#nextid'];
      } else {
        batchEntries.push(DeficiencyTicketWatcher.fromDto(item));
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch deficiency ticket watchers: ',
      batchEntries.length,
      'Total',
    );

    await this.deficiencyTicketWatchersDaoService.saveDeficiencyTicketWatchers(batchEntries);

    const allEntries = entries.concat(batchEntries);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more deficiency ticket watchers with nextid',
        nextId,
      );
      return await this.loadRemoteDeficiencyTicketWatcherBatch(
        syncKey,
        deficiencyTicketIdList,
        allEntries,
        nextId,
      );
    }

    return allEntries;
  }
}
